import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MailLine from '@/assets/icons/mail_line.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import TextField from '@/components/atoms/TextField';

import { PublicVideoLayout } from '../PublicVideoLayout';

import styles from '../public.module.scss';

interface ResetPasswordRequestProps {
    newPasswordRequest: (props: { email: string }) => Promise<unknown>;
}

export function ResetPasswordRequest({ newPasswordRequest }: ResetPasswordRequestProps) {
    const [email, setEmail] = useState('');
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<ReactNode>(null);
    const { t } = useTranslation();

    const onSubmit = async () => {
        setError(null);
        try {
            await newPasswordRequest({ email });
            setSuccess(true);
        } catch (e: unknown) {
            console.log(e);
            setError(apiErrorContent(e));
        }
    };

    return (
        <PublicVideoLayout>
            <h2>{t('publicPages.resetPasswordRequest.forgotPassword')}</h2>
            <h3>{t('publicPages.resetPasswordRequest.noWorriesWeLlSend')}</h3>
            <FormField label={t('publicPages.common.email')} labelClassName={styles.inverseLabel}>
                <TextField
                    prefix={<SvgIcon icon={MailLine} />}
                    groupClassName={styles.inputGroup}
                    adornmentClassName={styles.inputGroupText}
                    type="email"
                    data-testid="email"
                    name="email"
                    placeholder={t('publicPages.common.email')}
                    className={styles.formControl}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
            </FormField>
            {success ? (
                <Alert tone="success">
                    {t('publicPages.resetPasswordRequest.pleaseCheckYourEmailFor')}.
                </Alert>
            ) : (
                ''
            )}
            {error ? <Alert tone="danger">{error}</Alert> : ''}
            <div className={styles.actions}>
                <Button
                    variant="primary"
                    data-testid="submitButton"
                    onClick={onSubmit}
                    fullWidth
                    disabled={email.trim() === ''}>
                    {t('publicPages.common.resetPassword')}
                </Button>
            </div>
        </PublicVideoLayout>
    );
}
